<template>
	<div class="layout-editor">
		<h2 class="content-block expanded" style="display:flex;justify-content:space-between;background-color:#ffc10799;padding-top:10px;padding-bottom:10px;">
			<div id="layout-editor-title">
				{{ page && page.title ? page.title : '' }} 
				<i class="dx-icon dx-icon-optionsgear pointer" style="font-size:20px;color:var(--dx-color-link);" @click="editPageSettings = !editPageSettings"></i>
			</div>
			<div v-if="page && page.shouldUseContext()" style="display: flex; gap: 10px; height: 40px; margin-top: -2px; align-items: center;">
				<DxSelectBox 
					:data-source="contextSchemaOptions" 
					value-expr="type" 
					display-expr="singular" 
					v-model="selectedContextSchema"
					placeholder="Select blueprint..." 
					width="180px"
					:show-clear-button="true"
					@value-changed="onContextSchemaChanged"
				/>
				<DxLookup 
					v-if="selectedContextSchema"
					:data-source="contextMoteOptions" 
					value-expr="id" 
					display-expr="_displayName" 
					v-model="selectedContextMoteId"
					placeholder="Select item..." 
					width="220px" 
					:search-enabled="true"
					:show-clear-button="true"
					@value-changed="onContextMoteChanged"
				/>
				<span v-if="page.context" style="color: #666; font-size: 12px;">
					Simulating: {{ page.context.moteTitle() }}
				</span>
			</div>
			<div style="display:flex;gap:10px;">
				<dx-button :type="isDirty ? 'danger' : 'normal'" :text="isDirty ? 'Cancel' : 'Back'" :icon="isDirty ? 'undo' : 'back'" @click="cancelLayoutChanges" />
				<dx-button type="default" text="Save" icon="save" @click="saveLayoutToPage" />
			</div>
		</h2>

		<div v-if="!isLoading && !fatalError && page && group && editPageSettings">
			<page-settings :page="page" :group="group" :show-cancel="true" @cancel="editPageSettings = false" @updated="editPageSettings = false; handlePageSettingsUpdate()" />
		</div>

		<DxPopup :visible="!!activeEditCell" :defer-rendering="true" @hiding="activeEditCell = null" :show-close-button="true" :resize-enabled="true" :title="'Editing ' + (activeEditCell ? activeEditCell.type : 'cell')" :toolbar-items="cellEditorToolbar" :width="editorPopupMax ? '99vw' : '80vw'" :height="editorPopupMax ? '99vh' : '80vh'">
			<template #content>
				<ux-settings-editor v-if="activeEditCell" :type="activeEditCell.type" :existing-settings="componentConfig[activeEditCell.id] || null" :recommended-width="recommendedWidth" :use-recommended-width="useRecommendedWidth" :context="page"
					@saveSettings="saveCellSettings" @deleteCell="clearCell" />
			</template>
		</DxPopup>

		<DxPopup :visible="!!actionEditorGraph" :defer-rendering="true" :show-title="true" title="Action Editor" :show-close-button="true" width="80%" height="80%" @hidden="actionEditorGraph = null" :wrapper-attr="{ class: 'no-padding-popup' }">
			<template #content>
				<ActionEditor v-if="actionEditorGraph" :graph-id="actionEditorGraph.id" :startup-event="actionEditorGraph.startupEvent" @editorFinished="actionEditorGraph = null" />
			</template>
		</DxPopup>

		<!-- Tab Settings Modal -->
		<DxPopup 
			:visible="!!tabSettingsModal" 
			:defer-rendering="true" 
			@hiding="tabSettingsModal = null" 
			:show-close-button="true" 
			:show-title="true"
			title="Tab Settings"
			width="400"
			height="auto"
			:drag-enabled="true"
		>
			<template #content>
				<div v-if="tabSettingsModal" class="tab-settings-modal">
					<div class="tab-settings-field">
						<label>Tab Title</label>
						<DxTextBox :value="tabSettingsModal.tabData.tabTitle" @value-changed="updateTabTitleFromModal($event.value)" placeholder="Enter tab title..." />
					</div>
					<div class="tab-settings-field">
						<label>Tab Icon</label>
						<DxLookup 
							:value="tabSettingsModal.tabData.tabIcon" 
							@value-changed="updateTabIconFromModal($event.value)"
							v-bind="tabIconLookupEditor"
							placeholder="Select icon..."
						>
							<template #tabIconField="{ data }">
								<div class="icon-field-display">
									<i v-if="data" :class="'dx-icon-' + data" style="font-size: 18px;"></i>
									<span v-if="data" style="margin-left: 10px;">{{ data }}</span>
								</div>
							</template>
							<template #tabIconItem="{ data }">
								<div style="display: flex; align-items: center; gap: 10px;">
									<i :class="'dx-icon-' + data" style="font-size: 24px;"></i>
									<span>{{ data }}</span>
								</div>
							</template>
						</DxLookup>
					</div>
					<div class="tab-settings-actions">
						<DxButton text="Done" type="default" @click="tabSettingsModal = null" />
						<DxButton text="Delete Tab" type="danger" stylingMode="text" @click="deleteTabFromModal()" />
					</div>
				</div>
			</template>
		</DxPopup>

		<DxLoadPanel v-if="isLoading" :position="{ of: '.primary-scrollview' }" :visible="true" :show-indicator="true" :show-pane="true" shading-color="rgba(0,0,0,0.4)" />
		<div v-if="fatalError" class="content-block dx-error-message">
			<i class="dx-icon-warning"></i>
			<span class="dx-error-message-text">{{ fatalError }}</span>
		</div>

		<DxSplitter height="calc(100vh - 250px)" :dataSource="layoutDataSource" :orientation="layoutTree && layoutTree.direction ? layoutTree.direction : 'horizontal'" @resize-end="resizedSplitter" ref="layoutSplitter" v-if="!isLoading && !fatalError && page">
			<template #empty-card="{ data }">
				<div class="dx-card empty-cell-container" :data-cellref="data.id">
					<div class="cell-content">
						<div style="background:#ccc;border:1px dashed #aaa;padding:5px;display:flex;justify-content:center;align-items:center;height:100%;">
							<DxDropDownButton icon="add" text="Fill" :items="uxComponentsList" key-expr="id" display-expr="text" :element-attr="{ class: 'layoutTemplateFill' }" @itemClick="fillCell(data, $event)" />
						</div>
					</div>
					<div class="overlay-buttons">
						<div class="top-buttons">
							<DxButton text="🡅 Row Above 🡅" stylingMode="text" type="default" @click="addColumnOrRow(data, 'row-above')" class="overlay-button" />
						</div>
						<div class="left-buttons">
							<DxButton text="🡇 Col Left 🡇" stylingMode="text" type="default" @click="addColumnOrRow(data, 'col-left')" class="overlay-button column-button" />
						</div>
						<div class="right-buttons">
							<DxButton text="🡅 Col Right 🡅" stylingMode="text" type="default" @click="addColumnOrRow(data, 'col-right')" class="overlay-button column-button" />
						</div>
						<div class="bottom-buttons">
							<DxButton text="🡇 Row Below 🡇" stylingMode="text" type="default" @click="addColumnOrRow(data, 'row-below')" class="overlay-button" />
						</div>
					</div>
				</div>
			</template>
		<template #render-cell="{ data }">
			<page-layout-cell :type="data.celltype" :settings="componentConfig[data.id]" :cellData="data" :page="page"  @addColumnOrRow="addColumnOrRow(data, $event)" @editCell="editCell(data)" @actionEditorAdd="handleActionEditorInvoke" @actionEditorEdit="handleActionEditorInvoke" />
		</template>
		<template #render-tabs="{ data }">
			<div class="dx-card tabs-container" :data-cellref="data.id">
				<DxTabPanel 
					:items="getTabItemsWithSentinel(data.tabItems)" 
					:selected-index="getTabSelectedIndex(data.id)"
					@option-changed="handleTabOptionChanged(data.id, $event)"
					:animation-enabled="false"
					:swipe-enabled="false"
					item-title-template="tabTitleTemplate"
					item-template="tabContentTemplate"
				>
					<template #tabTitleTemplate="{ data: tabData }">
						<!-- Sentinel "Add Tab" button -->
						<div v-if="tabData._isSentinel" class="tab-add-sentinel" @click.stop="addTabToContainer(data.id)">
							<i class="dx-icon-add"></i>
						</div>
						<!-- Regular tab header -->
						<div v-else class="tab-title-editor">
							<i v-if="tabData.tabIcon" :class="'dx-icon-' + tabData.tabIcon" style="margin-right: 5px;"></i>
							<span>{{ tabData.tabTitle || 'Untitled' }}</span>
							<i class="dx-icon-rename tab-settings-icon" @click.stop="openTabSettingsModal(data.id, tabData)"></i>
						</div>
					</template>
					<template #tabContentTemplate="{ data: tabData }">
						<!-- Sentinel tab has no content -->
						<div v-if="tabData._isSentinel" class="tab-sentinel-content">
							<p style="color: #999; text-align: center;">Click the + tab to add a new tab</p>
						</div>
						<!-- Regular tab content -->
						<div v-else class="tab-content-wrapper">
							<div class="tab-content-cell">
								<!-- Empty card inside tab -->
								<div v-if="tabData.type === 'empty-card'" class="tab-empty-cell">
									<DxDropDownButton icon="add" text="Fill Tab" :items="uxComponentsListForTabs" width="240px" key-expr="id" display-expr="text" @itemClick="fillTabCell(data.id, tabData.id, $event)" />
								</div>
								<!-- Actual content inside tab -->
								<page-layout-cell v-else 
									:type="tabData.type" 
									:settings="componentConfig[tabData.id]" 
									:cellData="tabData" 
									:page="page"
									:hide-layout-buttons="true"
									@editCell="editTabCell(tabData)"
									@actionEditorAdd="handleActionEditorInvoke" 
									@actionEditorEdit="handleActionEditorInvoke"
								/>
							</div>
						</div>
					</template>
				</DxTabPanel>
				<div class="overlay-buttons">
					<div class="top-buttons">
						<DxButton text="🡅 Row Above 🡅" stylingMode="text" type="default" @click="addColumnOrRow(data, 'row-above')" class="overlay-button" />
					</div>
					<div class="left-buttons">
						<DxButton text="🡇 Col Left 🡇" stylingMode="text" type="default" @click="addColumnOrRow(data, 'col-left')" class="overlay-button column-button" />
					</div>
					<div class="right-buttons">
						<DxButton text="🡅 Col Right 🡅" stylingMode="text" type="default" @click="addColumnOrRow(data, 'col-right')" class="overlay-button column-button" />
					</div>
					<div class="bottom-buttons">
						<DxButton text="🡇 Row Below 🡇" stylingMode="text" type="default" @click="addColumnOrRow(data, 'row-below')" class="overlay-button" />
					</div>
				</div>
			</div>
		</template>
	</DxSplitter>
	</div>
</template>

<style lang="scss">
.layout-editor {
	.column-button .dx-button-text {
		white-space: pre-line;
		text-overflow: clip;
		overflow: hidden;
	}
	.column-button.dx-button-has-text {
		min-width: 36px;
		.dx-button-content {
			padding-block: 0px;
			padding-inline-start: 0px;
			padding-inline-end: 0px;	
		}
	}
	.layoutTemplateFill {
		max-width: 240px;
		width: 100%;
		margin: 0 auto;
	}
	.empty-cell-container {
		margin: 10px;
		padding: 10px;
		position: relative;
		
		.cell-content {
			width: 100%;
			height: 100%;
			min-height: 200px;
		}
		.overlay-buttons {
			position: absolute;
			top: 0; left: 0; right: 0; bottom: 0;
			pointer-events: none;
			z-index: 10;
		}
		.overlay-button {
			pointer-events: auto;
			background: rgba(255, 255, 255, 0.9);
			border: 1px solid #ddd;
			border-radius: 4px;
			font-size: 12px;
			padding: 4px 8px;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

			&:hover {
				background: rgba(255, 255, 255, 1);
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
			}
		}

		.top-buttons {
			position: absolute;
			top: 5px; left: 5px; right: 5px;
			display: flex;
			justify-content: center;
			align-items: center;
		}
		.left-buttons {
			position: absolute;
			left: 5px; top: 50%;
			transform: translateY(-50%);
		}
		.right-buttons {
			position: absolute;
			right: 5px; top: 50%;
			transform: translateY(-50%);
		}
		.bottom-buttons {
			position: absolute;
			bottom: 5px; left: 5px; right: 5px;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.column-button {
			writing-mode: vertical-rl;
			text-orientation: mixed;
			height: auto;
			white-space: pre-line;
		}
	}

	.tabs-container {
		margin: 10px;
		padding: 10px;
		position: relative;
		min-height: 300px;
		
		.tab-title-editor {
			display: flex;
			align-items: center;
			gap: 5px;
			
			.tab-settings-icon {
				cursor: pointer;
				opacity: 0.4;
				font-size: 14px;
				margin-left: 5px;
				transition: opacity 0.2s;
				
				&:hover {
					opacity: 1;
				}
			}
		}
		
		.tab-add-sentinel {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0 8px;
			cursor: pointer;
			opacity: 0.6;
			transition: opacity 0.2s;
			
			&:hover {
				opacity: 1;
			}
			
			i {
				font-size: 16px;
			}
		}
		
		.tab-sentinel-content {
			display: flex;
			justify-content: center;
			align-items: center;
			min-height: 100px;
		}
		
		.tab-content-wrapper {
			display: flex;
			flex-direction: column;
			height: 100%;
			min-height: 200px;
		}
		
		.tab-content-cell {
			flex-grow: 1;
			min-height: 150px;
		}
		
		.tab-empty-cell {
			display: flex;
			justify-content: center;
			align-items: center;
			height: 100%;
			min-height: 150px;
			background: #f5f5f5;
			border: 1px dashed #ccc;
			border-radius: 4px;
		}
		
		.overlay-buttons {
			position: absolute;
			top: 0; left: 0; right: 0; bottom: 0;
			pointer-events: none;
			z-index: 10;
		}
		.overlay-button {
			pointer-events: auto;
			background: rgba(255, 255, 255, 0.9);
			border: 1px solid #ddd;
			border-radius: 4px;
			font-size: 12px;
			padding: 4px 8px;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

			&:hover {
				background: rgba(255, 255, 255, 1);
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
			}
		}

		.top-buttons {
			position: absolute;
			top: 5px; left: 5px; right: 5px;
			display: flex;
			justify-content: center;
			align-items: center;
		}
		.left-buttons {
			position: absolute;
			left: 5px; top: 50%;
			transform: translateY(-50%);
		}
		.right-buttons {
			position: absolute;
			right: 5px; top: 50%;
			transform: translateY(-50%);
		}
		.bottom-buttons {
			position: absolute;
			bottom: 5px; left: 5px; right: 5px;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.column-button {
			writing-mode: vertical-rl;
			text-orientation: mixed;
			height: auto;
			white-space: pre-line;
		}
	}
}

.tab-settings-modal {
	padding: 10px;
	
	.tab-settings-field {
		margin-bottom: 20px;
		
		label {
			display: block;
			font-weight: 500;
			margin-bottom: 8px;
			color: var(--dx-color-text);
		}
	}
	
	.icon-field-display {
		display: flex;
		align-items: center;
	}
	
	.tab-settings-actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: 20px;
		padding-top: 15px;
		border-top: 1px solid var(--dx-color-border);
	}
}

// Icon lookup popup styling (same as page-settings)
.popup-iconselect.dx-lookup-popup-wrapper .dx-list-items {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	align-content: flex-start;
	justify-content: space-between;
	align-items: flex-start;
	> .dx-list-item {
		width: 200px;
		i {
			font-size: 24px;
		}
		span {
			line-height: 24px;
			vertical-align: top;
			margin-left: 5px;
		}
	}
	> .dx-list-item:first-of-type {
		margin-top: 0;
	}
}
</style>

<script setup>
/* eslint-disable */
import { ref, nextTick, inject, watch, computed } from 'vue';
import { DxSplitter, DxItem } from 'devextreme-vue/splitter';		// eslint-disable-line no-unused-vars
import { DxTabPanel, DxItem as DxTabItem } from 'devextreme-vue/tab-panel';	// eslint-disable-line no-unused-vars
import { DxTextBox } from 'devextreme-vue/text-box';
import CustomStore from 'devextreme/data/custom_store';
import PageLayoutCell from '../components/page-layout-cell';
import UxSettingsEditor from '../components/ux-settings-editor';
import DxButton from 'devextreme-vue/button';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxBox } from 'devextreme-vue/box';
import { DxPopup } from 'devextreme-vue/popup';
import { DxLookup } from 'devextreme-vue/lookup';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { Mote } from '@/mote';
import notify from 'devextreme/ui/notify';
import { alert as dxAlert, confirm as dxConfirm } from 'devextreme/ui/dialog';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import PageSettings from '../components/page-settings';
import Page from '@/pages';
import GroupCache from '@/groups';
import { uxComponentsList } from '../ux-components';
import ActionEditor from '../components/actions/action-editor.vue';
import icons from '../data/icons';

const $http = inject('$http');
const router = useRouter();
const route = useRoute();
const isLoading = ref(true);			// If we are loading any critical layout data, we can't show anything but a placeholder.
const fatalError = ref(null);			// If we have a fatal error from loading or a component, we override everything with an error message.
const page = ref(null);					// Page object instantiated from server data, or null if not loaded. Consists of layout, components, settings and other page-specific data.
const group = ref(null);				// Group object from cache or null if not loaded.
const isDirty = ref(false);				// Whether we have unsaved changes to the layout or settings.
const editPageSettings = ref(false);	// If we are editing the page settings - and should show the page-settings component.
const actionEditorGraph = ref(null);	// The graph details for the action editor. Consists of id and startupEvent, or null if not open.

// Add navigation guard before leaving the page to prompt if changes are outstanding.
onBeforeRouteLeave((to, from, next) => {
	if (!isDirty.value) {
		next();
		return;
	}
	dxConfirm('You have unsaved changes. Do you really want to leave?', 'Discard changes?').then(result => {
		next(!!result);
	});
});

// Watch for route changes (and also immediately once mounted) to fetch the full page layout.
watch( () => [route.params.groupRef, route.params.pageRef],
	async ([newGroupRef, newPageRef]) => {
		if (newGroupRef && newPageRef) {
			await fetchPageLayout(newGroupRef, newPageRef);
		}
	},
	{ immediate: true }
);

// Given a groupRef (group ID/slug) and pageRef (page ID/slug), load the full page to ready for editing.
async function fetchPageLayout(groupRef, pageRef) {
	isLoading.value = true;
	fatalError.value = null;			// Always reset our previous fatal error, since the way we exit an error state is to navigate elsewhere.

	try {
		const validFormat = /^[\w-]+$/;
		if (!validFormat.test(groupRef) || !validFormat.test(pageRef)) {
			throw new Error('Invalid group or page reference format');
		}
		const response = await $http.get(`/group-page/${groupRef}/${pageRef}`);
		page.value = new Page(response.data);
		// Hydrate the layout tree from the page object, recalculating the max layoutUid and reloading the data source if needed.
		if (!page.value.layout || !('id' in page.value.layout)) {
			layoutTree = JSON.parse(JSON.stringify(defaultLayoutTree));
			layoutUid = page.value.findHighestLayoutUid(defaultLayoutTree) + 1;
		} else {
			layoutTree = JSON.parse(JSON.stringify(page.value.layout));
			layoutUid = page.value.findHighestLayoutUid() + 1;
		}
		if (layoutTree.id != 0 || layoutTree.type != 'splitter' || !layoutTree.direction || !Array.isArray(layoutTree.items)) {
			throw new Error('Invalid layout tree');
		}
		componentConfig.value = page.value.components;
		group.value = GroupCache.getBySlug(groupRef);
		if (!group.value) {
			throw new Error('Group not loadable from cache');
		}
		if (layoutSplitter.value) {
			layoutSplitter.value.instance.getDataSource().reload();
		}
	} catch (err) {
		fatalError.value = err.message;
		console.error('Error fetching page layout:', err);
	} finally {
		isLoading.value = false;
	}
}

let layoutUid = 1;						// Unique ID for each cell in the layout tree.
const activeEditCell = ref(null);		// Cell object being edited. Should contain a id and type at least.
const layoutSplitter = ref();			// The main splitter object used to render the layout.
const componentConfig = ref({});		// Primary map used to house config options used in our compontents. Maps from the layoutUid to key-value object.
const recommendedWidth = ref(null);		// The recommended width for the active edit cell, if one is available.
const useRecommendedWidth = ref(false);	// Whether we should use the recommended width for the active edit cell.
const editorPopupMax = ref(false);		// If the editor popup should be maximized.

// Tabs editor state
const tabSelectedIndices = ref({});					// Map of tabs container ID -> currently selected tab index.
const tabSettingsModal = ref(null);					// Object with { containerId, tabData } for the tab settings modal, or null.

// Icon lookup editor options for tab icon picker (same style as page-settings)
const tabIconLookupEditor = {
	dataSource: icons,
	itemTemplate: 'tabIconItem',
	fieldTemplate: 'tabIconField',
	searchEnabled: true,
	showClearButton: true,
	usePopover: true,
	dropDownOptions: {
		wrapperAttr: {
			class: 'popup-iconselect',
		},
		deferRendering: false,
	},
};

// Components list filtered for tabs (excludes 'tabs' itself and 'delete')
const uxComponentsListForTabs = computed(() => {
	return uxComponentsList.filter(c => c.id !== 'tabs' && c.id !== 'delete');
});

// Get tab items with sentinel "add" tab appended
const getTabItemsWithSentinel = (tabItems) => {
	return [...tabItems, { _isSentinel: true, id: 'sentinel' }];
};

const resizedSplitter = () => {
	isDirty.value = true;
	syncLayoutTreeWithDataSource();
}

// The layout tree is the source of truth for the layout - a tree of cells. It is used to build the layoutDataSource, and to render the layout.
// These changes are persisted (but computed layoutDataSource is not). The root of our tree is always a splitter!
const defaultLayoutTree = {
	id: 0,						// Each cell has a unique incremental, with 0 being the root.
	type: 'splitter',			// The type of the cell: splitter, or one of the cell types.
	direction: 'vertical',		// The direction of the cell, if it is a splitter. Either 'vertical' or 'horizontal'.
	items: [],					// The children of the cell, if it is a splitter.
}
defaultLayoutTree.items.push({ id: layoutUid++, type: 'empty-card' });
let layoutTree = JSON.parse(JSON.stringify(defaultLayoutTree));

// Simple DFS search for a specific cell in our layoutTree. Returns object with 'node' reference and 'parents' array (unless id=0, should always have a parent).
function findInLayoutTree(id, node = layoutTree, parents = []) {
	if (node.id === id)
		return { node, parents };
	if (node.items && Array.isArray(node.items)) {
		for (const item of node.items) {
			const found = findInLayoutTree(id, item, [...parents, node]);
			if (found)
				return found;
		}
	}
	return null;
}

// Function to synchronize sizes of cells in our layoutTree with the sizes of the cells in our layoutDataSource.
// This can be used after the user drags a splitter to resize it; it is not trivial because dragging a splitter can resize
// multiple cells if they are in the way, from different splitters and different orientations. (We just do a full sync each time)
const syncLayoutTreeWithDataSource = () => {
	// We initially tried to get data from cells in layoutDataSource, but this doesn't seem to work since the 'size' values are not set upon change.
	// So instead, we set an attribute data-cellref on each cell and use that to find the corresponding sizes manually.
	// Note that we must gather data from both "cells" as well as the underlying splitter items, since these also have sizes.
	// The data-cellref attribute used here is set on splitters in the buildRenderTree function, and on cells in the actual templates above.
	const sizes = {};
	document.querySelectorAll('.dx-splitter-item-content[data-cellref]').forEach(cell => {
		let splitterItem = cell.closest('.dx-splitter-item');
		if (splitterItem) {
			const size = splitterItem.parentNode.classList.contains('dx-splitter-vertical') ? splitterItem.clientHeight : splitterItem.clientWidth;
			sizes[cell.dataset.cellref] = size;
		}
	});

	// Traverse every cell in our layoutTree, visiting each and setting its size to the corresponding value in our sizes map.
	function traverseLayoutTree(node) {
		if (node.id !== undefined && sizes[node.id] !== undefined) {
			node.size = sizes[node.id];
		}
		if (node.items) {
			for (const item of node.items) {
				traverseLayoutTree(item);
			}
		}
	}
	traverseLayoutTree(layoutTree);

	// Then regenerate the layoutDataSource from our layoutTree. NB: I don't think this is needed; might enable later?
	// layoutSplitter.value.instance.getDataSource().reload();
}

// Build the render data source used in a DxSplitter from our layoutTree. This is almost the same, but we have to use a splitter object to store the items,
// and a template property +  type of the cell.
function buildRenderTree() {
	const layoutRoot = { id: 0, splitter: { orientation: layoutTree.direction, items: [] } };
	function processBranch(iterate_branch, foreign_position) {
		for (var i = 0; i < iterate_branch.items.length; i++) {
			if (iterate_branch.items[i].type == 'splitter') {
				let new_branch = {
					id: iterate_branch.items[i].id,
					minSize: 150,
					size: iterate_branch.items[i].size,
					splitter: {
						orientation: iterate_branch.items[i].direction,
						elementAttr: {
							'data-cellref': iterate_branch.items[i].id,
						},
						items: [],
					},
				};
				foreign_position.splitter.items.push(new_branch);
				processBranch(iterate_branch.items[i], new_branch);
			} else if (iterate_branch.items[i].type == 'tabs') {
				// Tabs container: render using the render-tabs template, passing along tab items
				foreign_position.splitter.items.push({
					id: iterate_branch.items[i].id,
					template: 'render-tabs',
					celltype: 'tabs',
					tabItems: iterate_branch.items[i].items,		// Pass along the tab items for rendering
					minSize: 150,
					size: iterate_branch.items[i].size,
				});
			} else {
				foreign_position.splitter.items.push({
					id: iterate_branch.items[i].id,
					template: iterate_branch.items[i].type == 'empty-card' ? 'empty-card' :'render-cell',
					celltype: iterate_branch.items[i].type,
					minSize: 150,
					size: iterate_branch.items[i].size,
				});
			}
		}
	}
	processBranch(layoutTree, layoutRoot);
	console.log("Built render tree", layoutRoot);
	return layoutRoot;
}

const layoutDataSource = new CustomStore({ 
	key: 'id',
	loadMode: 'raw',
	cacheRawData: true,			// We'll cache the data we generate automatically for our byKey etc. functions.
	load: function(options) {
		return [ buildRenderTree() ];
	},
	byKey: function(key) {
		console.log("byKey request", key);
		console.log(layoutDataSource['__rawData']);
		//let foundCell = traverseAndFindCell(layoutDataSource['__rawData'], key);
		//console.log(foundCell);
		return null;
	},
});

// Clicked to fill an 'empty-card' cell with a specific type of cell. Optionally, the cell can also be deleted here, removing it from the layout completely.
const fillCell = (data, { itemData }) => {
	isDirty.value = true;
	let { node, parents } = findInLayoutTree(data.id);
	if (!node || node.type != 'empty-card') {
		console.error("Couldn't find cell " + data.id + " in layoutTree");
		return;
	}
	if (itemData.id == 'delete') {
		deleteCell(node, parents);
	} else if (itemData.id == 'tabs') {
		// Convert to a tabs container with one empty tab
		node.type = 'tabs';
		node.items = [{
			id: layoutUid++,
			type: 'empty-card',
			tabTitle: 'Untitled Tab',
			tabIcon: 'folder',
		}];
		// Initialize selected tab index
		tabSelectedIndices.value[data.id] = 0;
	} else {
		node.type = itemData.id;
		componentConfig.value[data.id] = uxComponentsList.find(c => c.id == itemData.id)?.defaultSettings ?? {};
		nextTick(() => {
			editCell(node);
		});
	}
	layoutSplitter.value.instance.getDataSource().reload();
}

// Delete a cell completely from the layout tree, removing it and it's parent splitter if it is empty.
// Values for node and parents can be found by calling findInLayoutTree. Returns boolean success state, caller should reload layout afterwards.
// Note: This function handles splitter children. Tab children should use removeTabFromContainer instead.
const deleteCell = (node, parents) => {
	isDirty.value = true;
	const parent = parents[parents.length - 1];
	
	// If parent is a tabs container, we can't use this function - tabs items should be deleted via removeTabFromContainer
	if (parent.type === 'tabs') {
		console.error("Can't delete tab item " + node.id + " using deleteCell - use removeTabFromContainer instead");
		return false;
	}
	
	if (parent.type !== 'splitter') {
		console.error("Can't delete cell " + node.id + " because it is not a child of a splitter");
		return false;
	}
	// If deleting this cell would leave the parent root splitter empty, we don't allow it. This means there'll always be at least one empty cell for our UI.
	const parentItems = parent.items;
	if (parentItems.length == 1 && parents.length == 1 && parents[0].id === 0) {
		console.error("Couldn't delete cell " + node.id + " because it is the only cell in the root splitter");
		return false;
	}
	const nodeIndex = parentItems.findIndex(item => item.id == node.id);
	if (nodeIndex !== -1) {
		parentItems.splice(nodeIndex, 1);
		// Remove the parent splitter as well if it is now empty.
		if (parentItems.length == 0 && parents.length >= 2) {
			parents[parents.length - 2].items.splice(parents[parents.length - 2].items.findIndex(item => item.id == parent.id), 1);
		}
	}
	return true;
}

// Add a column or row to a cell given a button press from the cell's UI. Adjacent type is one of: 'row-above', 'row-below', 'col-left', 'col-right'.
const addColumnOrRow = (data, adjacent_type) => {
	isDirty.value = true;
	
	const validTypes = ['row-above', 'row-below', 'col-left', 'col-right'];
	if (!validTypes.includes(adjacent_type)) {
		console.error("Invalid adjacent type " + adjacent_type);
		return;
	}
	const is_column = adjacent_type.startsWith('col-');
	const is_before = adjacent_type.endsWith('-above') || adjacent_type.endsWith('-left');
	
	let { node, parents} = findInLayoutTree(data.id);
	if (!node) {
		console.error("Couldn't find cell " + data.id + " in layoutTree");
		return;
	}

	const parent = parents[parents.length - 1];
	
	// If the parent is a tabs container, we don't support adding rows/cols to tab content
	// The row/col buttons on the tabs container itself will use the tabs container's parent (a splitter)
	if (parent.type === 'tabs') {
		notify({ message: 'Cannot add rows/columns inside a tab. Use the buttons on the tabs container instead.', type: 'warning', displayTime: 3000, position: 'top right' });
		return;
	}
	
	if (parent.type !== 'splitter') {
		console.error("Couldn't find splitter parent for cell " + data.id);
		return;
	}
	
	if ((parent.direction == 'horizontal' && is_column) || (parent.direction == 'vertical' && !is_column)) {
		// Adding a column to a horizontal splitter, or a row to a vertical splitter.
		const nodeIndex = parent.items.findIndex(item => item.id === node.id);
		const insertIndex = is_before ? nodeIndex : nodeIndex + 1;
		parent.items.splice(insertIndex, 0, { id: layoutUid++, type: 'empty-card' });
	} else {
		// Adding a row to a horizontal splitter, or a column to a vertical splitter.
		// In order to do this, we need to add a new splitter - replacing the current cell, then add both the old cell and our new cell to that instead.
		const newSplitter = { id: layoutUid++, type: 'splitter', direction: (is_column ? 'horizontal' : 'vertical'), items: [] };
		const nodeIndex = parent.items.findIndex(item => item.id === node.id);
		parent.items.splice(nodeIndex, 1, newSplitter);
		
		if (is_before) {
			newSplitter.items.push({ id: layoutUid++, type: 'empty-card' });
			newSplitter.items.push(node);
		} else {
			newSplitter.items.push(node);
			newSplitter.items.push({ id: layoutUid++, type: 'empty-card' });
		}
	}
	layoutSplitter.value.instance.getDataSource().reload();
}

const editCell = (node) => {
	let cell = findInLayoutTree(node.id);
	if (cell && cell.node) {
		// In addition to the data when editing a cell, we pass along a recommended width based on the current layout's width without the page-layout-cell's UI elements.
		let cellElement = document.querySelector('.dx-splitter-item-content[data-cellref="' + cell.node.id + '"]');
		recommendedWidth.value = cellElement ? cellElement.clientWidth : null;
		useRecommendedWidth.value = false;
		
		activeEditCell.value = cell.node;
	}
}

// Called when save button is clicked in our UX settings editor from our edit-cell popup.
const saveCellSettings = (ev) => {
	isDirty.value = true;
	// Apply edited config to our componentConfig store. Values input are already safely cloned and not refs.
	componentConfig.value[activeEditCell.value.id] = ev;
	// Automatically close the edit-cell popup.
	activeEditCell.value = null;
}

// Called when delete button is clicked in our UX settings editor from out edit-cell popup, to clear the current cell.
// The calling editor is responsible for verifying deletion.
const clearCell = () => {
	isDirty.value = true;
	const cellId = activeEditCell.value.id;
	activeEditCell.value = null;
	delete componentConfig.value[cellId];

	// Return template to empty-cell to allow another type to be added; the cell itself can also be deleted in this menu.
	const foundCell = findInLayoutTree(cellId);
	if (foundCell && foundCell.node) {
		foundCell.node.type = 'empty-card';
		layoutSplitter.value.instance.getDataSource().reload();
	}
}

// ============================================
// Tabs Management Functions
// ============================================

// Get the currently selected tab index for a tabs container
const getTabSelectedIndex = (containerId) => {
	return tabSelectedIndices.value[containerId] || 0;
};

// Handle tab selection change event from DxTabPanel
const handleTabOptionChanged = (containerId, event) => {
	if (event.name === 'selectedIndex') {
		// Prevent selecting the sentinel tab (last item)
		const found = findInLayoutTree(containerId);
		if (found && found.node.type === 'tabs') {
			const realTabCount = found.node.items.length;
			if (event.value >= realTabCount) {
				// User clicked sentinel, keep previous selection
				return;
			}
		}
		tabSelectedIndices.value[containerId] = event.value;
	}
};

// Add a new tab to a tabs container
const addTabToContainer = (containerId) => {
	isDirty.value = true;
	const found = findInLayoutTree(containerId);
	if (!found || found.node.type !== 'tabs') {
		console.error("Couldn't find tabs container " + containerId);
		return;
	}
	
	const tabsNode = found.node;
	const newTabId = layoutUid++;
	const newTab = {
		id: newTabId,
		type: 'empty-card',
		tabTitle: 'New Tab',
		tabIcon: 'folder',
	};
	tabsNode.items.push(newTab);
	
	// Select the newly added tab
	tabSelectedIndices.value[containerId] = tabsNode.items.length - 1;
	
	layoutSplitter.value.instance.getDataSource().reload();
};

// Remove a tab from a tabs container
const removeTabFromContainer = async (containerId, tabId) => {
	const found = findInLayoutTree(containerId);
	if (!found || found.node.type !== 'tabs') {
		console.error("Couldn't find tabs container " + containerId);
		return;
	}
	
	const tabsNode = found.node;
	const tabIndex = tabsNode.items.findIndex(t => t.id === tabId);
	if (tabIndex === -1) {
		console.error("Couldn't find tab " + tabId + " in container " + containerId);
		return;
	}
	
	// Don't allow deleting the last tab - convert back to empty-card instead
	if (tabsNode.items.length === 1) {
		const confirmed = await dxConfirm('This is the last tab. Removing it will convert the tabs container back to an empty cell. Continue?', 'Remove Last Tab?');
		if (!confirmed) return;
		
		isDirty.value = true;
		// Convert tabs container back to empty-card
		tabsNode.type = 'empty-card';
		delete tabsNode.items;
		delete tabsNode.tabTitle;
		delete tabsNode.tabIcon;
		delete componentConfig.value[tabId];
		delete tabSelectedIndices.value[containerId];
		layoutSplitter.value.instance.getDataSource().reload();
		return;
	}
	
	// Confirm deletion if the tab has content
	const tabNode = tabsNode.items[tabIndex];
	if (tabNode.type !== 'empty-card') {
		const confirmed = await dxConfirm('This tab has content. Are you sure you want to delete it?', 'Delete Tab?');
		if (!confirmed) return;
	}
	
	isDirty.value = true;
	tabsNode.items.splice(tabIndex, 1);
	delete componentConfig.value[tabId];
	
	// Adjust selected index if needed
	const currentIndex = tabSelectedIndices.value[containerId] || 0;
	if (currentIndex >= tabsNode.items.length) {
		tabSelectedIndices.value[containerId] = Math.max(0, tabsNode.items.length - 1);
	}
	
	layoutSplitter.value.instance.getDataSource().reload();
};

// Fill a tab's empty cell with a component type
const fillTabCell = (containerId, tabId, { itemData }) => {
	isDirty.value = true;
	const found = findInLayoutTree(tabId);
	if (!found || found.node.type !== 'empty-card') {
		console.error("Couldn't find empty tab cell " + tabId);
		return;
	}
	
	found.node.type = itemData.id;
	componentConfig.value[tabId] = uxComponentsList.find(c => c.id === itemData.id)?.defaultSettings ?? {};
	
	nextTick(() => {
		editTabCell(found.node);
	});
	
	layoutSplitter.value.instance.getDataSource().reload();
};

// Edit a tab's cell content (opens the settings editor popup)
const editTabCell = (tabData) => {
	const found = findInLayoutTree(tabData.id);
	if (found && found.node) {
		let cellElement = document.querySelector('.tab-content-cell');
		recommendedWidth.value = cellElement ? cellElement.clientWidth : null;
		useRecommendedWidth.value = false;
		activeEditCell.value = found.node;
	}
};

// Open the tab settings modal
const openTabSettingsModal = (containerId, tabData) => {
	tabSettingsModal.value = {
		containerId,
		tabData: { ...tabData }	// Clone to avoid direct mutation
	};
};

// Update tab title from modal
const updateTabTitleFromModal = (newTitle) => {
	if (!tabSettingsModal.value) return;
	isDirty.value = true;
	
	const found = findInLayoutTree(tabSettingsModal.value.tabData.id);
	if (found && found.node) {
		found.node.tabTitle = newTitle;
		tabSettingsModal.value.tabData.tabTitle = newTitle;
		layoutSplitter.value.instance.getDataSource().reload();
	}
};

// Update tab icon from modal
const updateTabIconFromModal = (newIcon) => {
	if (!tabSettingsModal.value) return;
	isDirty.value = true;
	
	const found = findInLayoutTree(tabSettingsModal.value.tabData.id);
	if (found && found.node) {
		found.node.tabIcon = newIcon;
		tabSettingsModal.value.tabData.tabIcon = newIcon;
		layoutSplitter.value.instance.getDataSource().reload();
	}
};

// Delete tab from modal
const deleteTabFromModal = async () => {
	if (!tabSettingsModal.value) return;
	
	const { containerId, tabData } = tabSettingsModal.value;
	tabSettingsModal.value = null;	// Close modal first
	
	await removeTabFromContainer(containerId, tabData.id);
};

// Called to save the entire layout tree to the server.
const saveLayoutToPage = () => {
	for (const cellId in componentConfig.value) {
		if (Array.isArray(componentConfig.value[cellId])) {
			console.warn("Component configuration for cell " + cellId + " is an array, not an object -- may cause issues with saving.");
		}
	}
	
	$http.patch(`/page/${page.value.id}`, { 
		layout: layoutTree, 
		components: componentConfig.value 
	}).then(response => {
		console.log("Page updated:", response);
		notify({ message: "Changes saved successfully", type: "success", displayTime: 2000, position: "top right" });
		isDirty.value = false;
		// TODO: Handle sequence numbers from response!
		// Because we only change layout/settings, we don't need to reload the page or deal with caches.
	}).catch(error => {
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to update page'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	});
}

// Revert/Go Back button. Everything is kept in memory, so just don't save anything and navigate back to our page we were editing. Prompting for unsaved changes is handled by the navigation guard.
const cancelLayoutChanges = () => {
	router.push(`/${route.params.groupRef}/${route.params.pageRef}`);
}

// Toolbar items for editing cell popup
const cellEditorToolbar = computed(() => {
	return [{
		location: 'after',
		widget: 'dxButton',
		options: { icon: 'expandform', stylingMode: useRecommendedWidth.value ? 'contained' : 'outlined', text: 'Expand to real width', onClick: () => {
			if (activeEditCell.value && recommendedWidth.value) {
				useRecommendedWidth.value = !useRecommendedWidth.value;
			}
		} },
	}, {
		location: 'after',
		widget: 'dxButton',
		options: { icon: 'fullscreen', onClick: () => {
			editorPopupMax.value = !editorPopupMax.value;
		} },
	}];
});

// Handle an update for the page settings.
const handlePageSettingsUpdate = () => {
	// TODO: We need some way to make sure everything is okay. Does everything still work? We might have changed our slug or something like that...
	// TODO: But right now, we just force the GroupCache to refresh/reload the page.
	GroupCache.fetchGroupList();
	window.location.reload();
}

// Context simulation state and methods.
const selectedContextSchema = ref(null);		// Selected schema type string (e.g., 'customer')
const selectedContextMoteId = ref(null);		// Selected mote ID
const contextMoteOptions = ref([]);				// Motes loaded for the selected schema

// Computed list of allowed schemas for context simulation, derived from page settings.
const contextSchemaOptions = computed(() => {
	if (!page.value || !page.value.shouldUseContext()) {
		return [];
	}
	return page.value.getContextSchemas().filter(Boolean);
});

// When the schema selection changes, load motes for that schema type.
const onContextSchemaChanged = async (e) => {
	// Reset mote selection and context
	selectedContextMoteId.value = null;
	contextMoteOptions.value = [];
	if (page.value) {
		page.value.context = null;
	}
	
	if (!e.value) {
		return;
	}
	
	try {
		const response = await $http.get('/mote', { params: { type: e.value } });
		// Add a display name to each mote for the lookup
		const motes = response.data?.motes || [];
		contextMoteOptions.value = motes.map(mote => ({
			...mote,
			_displayName: mote.payload?.title || mote.payload?.name || `#${mote.id}`
		}));
	} catch (err) {
		console.error('Error fetching motes for context simulation:', err);
		notify({ message: 'Failed to load items for simulation', type: 'error', displayTime: 3000, position: 'top right' });
	}
};

// When a mote is selected, set it as the page context for simulation.
const onContextMoteChanged = (e) => {
	if (!e.value || !page.value) {
		if (page.value) {
			page.value.context = null;
		}
		return;
	}
	
	// Find the full mote data from our loaded options
	const moteData = contextMoteOptions.value.find(m => m.id === e.value);
	if (moteData) {
		// Create a Mote instance and set it as the page context
		page.value.context = new Mote(moteData);
		console.log('Context simulation set:', page.value.context);
		notify({ message: `Simulating context: ${page.value.context.moteTitle()}`, type: 'info', displayTime: 2000, position: 'top right' });
	}
};

// Handle an event to start our action editor from a named event in our ux component (for adding or editing actions).
const handleActionEditorInvoke = (eventDetails) => {
	console.log("Action editor invoke event", eventDetails);
	if (eventDetails.method == 'add') {
		actionEditorGraph.value = { id: null, startupEvent: eventDetails.type };
	} else if (eventDetails.method == 'edit') {
		actionEditorGraph.value = { id: eventDetails.actionId, startupEvent: null };
	}
}

</script>
